import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { getIssue, listIssues, updateIssue } from '../lib/api';
import { useFetch } from '../lib/use-fetch';
import { useCurrentUser } from '../lib/auth';
import { formatDateTime, relativeTime } from '../lib/format';
import { toast } from '../lib/toast';
import type { IssueApp, IssueDetail, IssueKind, IssueStatus, IssueSummary } from '../types/issues';
import ErrorState from './ErrorState';
import EmptyState from './ui/EmptyState';
import { SkeletonRow } from './ui/Skeleton';

/**
 * Issues page: every "Report a problem" message and every group of
 * automatic errors from the three apps and the pipeline. Lead moderators
 * and admins only (the API enforces this too).
 *
 *   #/issues        the list, filtered by status
 *   #/issues/<id>   one issue with its details, status and note
 */

type Filter = IssueStatus | 'all';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'handled', label: 'Handled' },
  { value: 'ignored', label: 'Ignored' },
  { value: 'all', label: 'All' },
];

const STATUS_LABEL: Record<IssueStatus, string> = {
  open: 'Open',
  handled: 'Handled',
  ignored: 'Ignored',
};

const STATUS_TONE: Record<IssueStatus, string> = {
  open: 'amber',
  handled: 'green',
  ignored: 'grey',
};

const STATUS_HINT: Record<IssueStatus, string> = {
  open: 'Still needs looking at',
  handled: 'Fixed or answered',
  ignored: 'Not worth acting on',
};

const KIND_LABEL: Record<IssueKind, string> = {
  report: 'Report',
  error: 'Error',
  pipeline: 'Pipeline failure',
};

const APP_LABEL: Record<IssueApp, string> = {
  display: 'Archive site',
  upload: 'Upload form',
  moderation: 'Moderation tool',
  pipeline: 'Processing pipeline',
};

function timesLabel(count: number): string {
  return count === 1 ? 'Once' : `${count} times`;
}

function StatusBadge({ issue }: { issue: Pick<IssueSummary, 'status' | 'recurred'> }) {
  return (
    <>
      <span
        className={`status-badge status-badge--${STATUS_TONE[issue.status]}`}
        data-testid="issue-status"
      >
        {STATUS_LABEL[issue.status]}
      </span>
      {issue.recurred && (
        <span className="status-badge status-badge--red" data-testid="issue-recurred">
          Happened again
        </span>
      )}
    </>
  );
}

function useCanSeeIssues(): boolean {
  const role = useCurrentUser()?.role ?? 'volunteer';
  return role === 'lead_moderator' || role === 'admin';
}

interface IssuesPageProps {
  /** Set when the route is #/issues/<id>. */
  issueId?: string;
  onNavigate: (hash: string) => void;
}

export default function IssuesPage({ issueId, onNavigate }: IssuesPageProps) {
  const canSee = useCanSeeIssues();
  if (!canSee) {
    return (
      <div className="issues-page">
        <h1>Issues</h1>
        <EmptyState
          testId="issues-forbidden"
          title="Only lead moderators and admins can see issues."
          description="If something is broken, use Report a problem and we will look into it."
        />
      </div>
    );
  }
  if (issueId) {
    return <IssueDetailView id={issueId} onNavigate={onNavigate} />;
  }
  return <IssueList onNavigate={onNavigate} />;
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

function IssueList({ onNavigate }: { onNavigate: (hash: string) => void }) {
  const [filter, setFilter] = useState<Filter>('open');
  const { data, loading, error, refresh } = useFetch(() => listIssues(), []);
  const items = useMemo(() => data?.items ?? [], [data]);

  const counts = useMemo(() => {
    const c: Record<Filter, number> = { open: 0, handled: 0, ignored: 0, all: items.length };
    for (const item of items) c[item.status] += 1;
    return c;
  }, [items]);

  const shown = filter === 'all' ? items : items.filter((i) => i.status === filter);

  return (
    <div className="issues-page">
      <div className="issues-page__header">
        <h1>Issues</h1>
        <button type="button" className="btn btn-secondary" onClick={refresh} data-testid="issues-refresh">
          Refresh
        </button>
      </div>
      <p className="issues-page__intro">
        Problems people reported and errors the apps caught. Repeated errors are grouped, so one bug
        is one row. Reports and errors are kept for 180 days.
      </p>

      <div className="issues-filter" role="group" aria-label="Show issues">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={`issues-filter__option${filter === f.value ? ' is-active' : ''}`}
            aria-pressed={filter === f.value}
            onClick={() => setFilter(f.value)}
            data-testid={`issues-filter-${f.value}`}
          >
            {f.label}
            {!loading && !error && <span className="issues-filter__count">{counts[f.value]}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <table className="data-table" aria-busy="true" data-testid="issues-loading">
          <tbody>
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonRow key={i} cells={3} />
            ))}
          </tbody>
        </table>
      ) : error ? (
        <ErrorState
          message="We could not load the issues."
          detail={error}
          onRetry={refresh}
          testId="issues-error"
        />
      ) : shown.length === 0 ? (
        <EmptyState
          testId="issues-empty"
          title={filter === 'open' ? 'Nothing open. All clear.' : 'No issues here.'}
          description={
            filter === 'open' && items.length > 0
              ? 'Everything reported has been handled or ignored.'
              : 'Reports from the Report a problem button and errors the apps catch will appear here.'
          }
        />
      ) : (
        <ul className="issues-list" data-testid="issues-list">
          {shown.map((issue) => (
            <li key={issue.id}>
              <a
                href={`#/issues/${encodeURIComponent(issue.id)}`}
                className="issue-row"
                data-testid="issue-row"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate(`#/issues/${encodeURIComponent(issue.id)}`);
                }}
              >
                <span className="issue-row__title">{issue.title}</span>
                <span className="issue-row__badges">
                  <StatusBadge issue={issue} />
                </span>
                <span className="issue-row__meta">
                  <span>{KIND_LABEL[issue.kind]}</span>
                  <span>{APP_LABEL[issue.app] ?? issue.app}</span>
                  {issue.kind !== 'report' && <span>{timesLabel(issue.count)}</span>}
                  {issue.canReply && <span>Can reply</span>}
                  <span title={formatDateTime(issue.lastSeen)}>{relativeTime(issue.lastSeen)}</span>
                  <span className="issue-row__ref">{issue.ref}</span>
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

function IssueDetailView({ id, onNavigate }: { id: string; onNavigate: (hash: string) => void }) {
  const { data, loading, error, refresh } = useFetch(() => getIssue(id), [id]);
  const [issue, setIssue] = useState<IssueDetail | null>(null);
  useEffect(() => setIssue(data), [data]);

  const back = (
    <a
      href="#/issues"
      className="issue-detail__back"
      onClick={(e) => {
        e.preventDefault();
        onNavigate('#/issues');
      }}
      data-testid="issue-back"
    >
      Back to issues
    </a>
  );

  if (loading && !issue) {
    return (
      <div className="issues-page" aria-busy="true" data-testid="issue-loading">
        {back}
        <p>Loading the issue...</p>
      </div>
    );
  }
  if (error || !issue) {
    const notFound = error?.startsWith('API error 404') ?? false;
    return (
      <div className="issues-page">
        {back}
        {notFound ? (
          <EmptyState
            testId="issue-not-found"
            title="We could not find that issue."
            description="It may have expired. Issues are kept for 180 days."
          />
        ) : (
          <ErrorState
            message="We could not load this issue."
            detail={error ?? undefined}
            onRetry={refresh}
            testId="issue-error"
          />
        )}
      </div>
    );
  }

  const { record } = issue;
  const isReport = issue.kind === 'report';

  return (
    <div className="issues-page issue-detail" data-testid="issue-detail">
      {back}
      <h1 className="issue-detail__title">{issue.title}</h1>
      <div className="issue-detail__badges">
        <StatusBadge issue={issue} />
        <span className="chip">{KIND_LABEL[issue.kind]}</span>
        <span className="chip">{APP_LABEL[issue.app] ?? issue.app}</span>
      </div>

      {issue.recurred && (
        <p className="issue-detail__callout" role="note">
          This was marked handled, then happened again. The fix may not have worked.
        </p>
      )}

      <div className="issue-detail__grid">
        <div className="issue-detail__main">
          {isReport && (
            <section className="issue-section">
              <h2>What they said</h2>
              <p className="issue-detail__message">{record.message || '(no message)'}</p>
              {record.email ? (
                <p>
                  <a
                    className="btn btn-secondary"
                    href={`mailto:${record.email}?subject=${encodeURIComponent(
                      `Your Pride History report ${record.ref}`,
                    )}`}
                    data-testid="issue-reply"
                  >
                    Reply to {record.email}
                  </a>
                </p>
              ) : (
                <p className="issue-detail__muted">They did not leave an email, so there is no one to reply to.</p>
              )}
            </section>
          )}

          {issue.relatedIssueId && (
            <section className="issue-section">
              <h2>Error they saw</h2>
              <p>
                They sent this just after an error ({record.relatedRef}).{' '}
                <a
                  href={`#/issues/${encodeURIComponent(issue.relatedIssueId)}`}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate(`#/issues/${encodeURIComponent(issue.relatedIssueId!)}`);
                  }}
                  data-testid="issue-related"
                >
                  Open that error
                </a>
              </p>
            </section>
          )}

          {record.error && (
            <section className="issue-section">
              <h2>{isReport ? 'Error details' : 'Latest occurrence'}</h2>
              <p className="issue-detail__error">
                {record.error.name ? `${record.error.name}: ` : ''}
                {record.error.message}
              </p>
              {record.error.stack && <pre className="issue-detail__pre">{record.error.stack}</pre>}
            </section>
          )}

          {record.context && Object.keys(record.context).length > 0 && (
            <section className="issue-section">
              <h2>Context</h2>
              <dl className="issue-detail__facts">
                {Object.entries(record.context).map(([k, v]) => (
                  <div key={k}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {record.breadcrumbs && record.breadcrumbs.length > 0 && (
            <section className="issue-section">
              <details>
                <summary>What happened before ({record.breadcrumbs.length} steps)</summary>
                <ol className="issue-detail__crumbs">
                  {record.breadcrumbs.map((b, i) => (
                    <li key={i}>
                      <span className="issue-detail__muted">{formatDateTime(b.at)}</span>{' '}
                      <span className="chip">{b.type}</span> {b.message}
                    </li>
                  ))}
                </ol>
              </details>
            </section>
          )}
        </div>

        <aside className="issue-detail__side">
          <TriageForm issue={issue} onSaved={setIssue} />
          <section className="issue-section">
            <h2>Details</h2>
            <dl className="issue-detail__facts">
              <Fact label="Reference" value={issue.ref} mono />
              {!isReport && <Fact label="Happened" value={timesLabel(issue.count)} />}
              {!isReport && <Fact label="First seen" value={formatDateTime(issue.firstSeen)} />}
              <Fact label={isReport ? 'Sent' : 'Last seen'} value={formatDateTime(issue.lastSeen)} />
              <Fact label="Page" value={record.page} />
              <Fact label="Browser" value={record.userAgent} />
              <Fact label="Screen" value={record.viewport} />
              <Fact label="Language" value={record.language} />
              <Fact label="Release" value={record.release} mono />
              {record.submissionId && (
                <div>
                  <dt>Submission</dt>
                  <dd>
                    <a
                      href={`#/submissions/${encodeURIComponent(record.submissionId)}`}
                      onClick={(e) => {
                        e.preventDefault();
                        onNavigate(`#/submissions/${encodeURIComponent(record.submissionId!)}`);
                      }}
                    >
                      {record.submissionId}
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </section>
        </aside>
      </div>
    </div>
  );
}

function Fact({ label, value, mono }: { label: string; value?: string; mono?: boolean }) {
  if (!value) return null;
  return (
    <div>
      <dt>{label}</dt>
      <dd className={mono ? 'issue-detail__mono' : undefined}>{value}</dd>
    </div>
  );
}

function TriageForm({ issue, onSaved }: { issue: IssueDetail; onSaved: (issue: IssueDetail) => void }) {
  const [status, setStatus] = useState<IssueStatus>(issue.status);
  const [note, setNote] = useState(issue.note ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatus(issue.status);
    setNote(issue.note ?? '');
  }, [issue.id, issue.status, issue.note]);

  const dirty = status !== issue.status || note.trim() !== (issue.note ?? '');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await updateIssue(issue.id, { status, note: note.trim() || undefined });
      onSaved(updated);
      toast.success(`Marked ${STATUS_LABEL[updated.status].toLowerCase()}.`);
    } catch {
      toast.error('We could not save that. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="issue-section issue-triage" onSubmit={handleSubmit} data-testid="issue-triage">
      <h2>Status</h2>
      <fieldset className="issue-triage__options">
        <legend className="sr-only">Status</legend>
        {(['open', 'handled', 'ignored'] as IssueStatus[]).map((s) => (
          <label key={s} className={`issue-triage__option${status === s ? ' is-active' : ''}`}>
            <input
              type="radio"
              name="issue-status"
              value={s}
              checked={status === s}
              onChange={() => setStatus(s)}
            />
            <span className="issue-triage__label">{STATUS_LABEL[s]}</span>
            <span className="issue-triage__hint">{STATUS_HINT[s]}</span>
          </label>
        ))}
      </fieldset>
      <label className="issue-triage__note-label" htmlFor="issue-note">
        Note <span className="issue-detail__muted">(optional)</span>
      </label>
      <textarea
        id="issue-note"
        className="issue-triage__note"
        rows={3}
        maxLength={2000}
        value={note}
        placeholder="What you did, or why it can wait"
        onChange={(e) => setNote(e.target.value)}
      />
      <button type="submit" className="btn btn-primary" disabled={!dirty || saving} data-testid="issue-save">
        {saving ? 'Saving...' : 'Save'}
      </button>
      {issue.updatedAt && (
        <p className="issue-detail__muted issue-triage__updated">
          Last changed {formatDateTime(issue.updatedAt)}
          {issue.updatedBy ? ` by ${issue.updatedBy}` : ''}.
        </p>
      )}
    </form>
  );
}
